import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import logo from '../../images/57logo.jpeg';
import { formatTime } from '../Utils/Utils';

const styles = StyleSheet.create({
    page: {
        padding: 30,
        fontFamily: 'Helvetica',
        fontSize: 10,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
        borderBottomWidth: 2,
        borderBottomColor: '#112131',
        paddingBottom: 10,
    },
    companyInfo: {
        flexDirection: 'column',
        alignItems: 'flex-end',
    },
    logo: {
        width: 100,
        height: 80,
    },
    title: {
        fontSize: 20,
        fontWeight: 'bold',
        color: '#112131',
        marginBottom: 5,
    },
    subtitle: {
        fontSize: 10,
        color: '#666',
    },
    infoBlock: {
        marginBottom: 10,
        padding: 10,
        backgroundColor: '#f8f8f8',
        borderRadius: 5,
    },
    infoRow: {
        flexDirection: 'row',
        marginBottom: 4,
    },
    infoLabel: {
        width: 120,
        fontWeight: 'bold',
    },
    infoValue: {
        flex: 1,
    },
    table: {
        marginTop: 20,
        borderWidth: 1,
        borderColor: '#112131',
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#112131',
        color: 'white',
        fontWeight: 'bold',
    },
    tableCellHeader: {
        padding: 4,
        fontSize: 9,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#ccc',
        alignItems: 'center',
    },
    tableCell: {
        padding: 4,
        fontSize: 9,
        flexWrap: 'wrap',
    },
});

const columnFlex = {
    index: 0.5,
    date: 1.2,
    day: 1,
    site: 1.5,
    designation: 1.3,
    shiftStart: 1,
    shiftEnd: 1,
    totalHours: 1,
    breakHours: 1,
    workingHours: 1.2,
};

const TimesheetPDF = ({ data = [], userDetails = {}, from, to }) => {
    const currentDate = new Date().toLocaleDateString('en-GB');

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* Header */}
                <View style={styles.header}>
                    <View>
                        <Text style={styles.title}>Timesheet Report</Text>
                        <Text style={styles.subtitle}>Generated on: {currentDate}</Text>
                    </View>
                    <View style={styles.companyInfo}>
                        <Image style={styles.logo} src={logo} />
                        <Text style={styles.subtitle}>57Pease Protection Limited</Text>
                    </View>
                </View>

                {/* Staff and Filter Info */}
                <View style={styles.infoBlock}>
                    <View style={styles.siteInfo}>
                    <View style={styles.infoRow}>
                            <Text style={styles.infoLabel}>Staff ID:</Text>
                            <Text style={styles.infoValue}>
                                {userDetails && `${userDetails.id || ''}`}
                            </Text>
                        </View>
                        <View style={styles.infoRow}>
                            <Text style={styles.infoLabel}>Staff Name:</Text>
                            <Text style={styles.infoValue}>
                                {userDetails ? `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() : 'N/A'}
                            </Text>
                        </View>
                        <View style={styles.infoRow}>
                            <Text style={styles.infoLabel}>Mobile Number:</Text>
                            <Text style={styles.infoValue}>
                                {userDetails?.mobileNo || 'N/A'}
                            </Text>
                        </View>
                        <View style={styles.infoRow}>
                            <Text style={styles.infoLabel}>Email ID:</Text>
                            <Text style={styles.infoValue}>
                                {userDetails?.emailId || 'N/A'}
                            </Text>
                        </View>
                    </View>
                </View>
                <View style={styles.infoBlock}>
                    <View style={styles.infoRow}>
                        <Text style={styles.infoLabel}>From:</Text>
                        <Text style={styles.infoValue}>{from}</Text>
                    </View>
                    <View style={styles.infoRow}>
                        <Text style={styles.infoLabel}>To:</Text>
                        <Text style={styles.infoValue}>{to}</Text>
                    </View>
                </View>

                {/* Timesheet Table */}
                <View style={styles.table}>
                    <View style={styles.tableHeader}>
                        <Text style={[styles.tableCellHeader, { flex: columnFlex.index }]}>#</Text>
                        <Text style={[styles.tableCellHeader, { flex: columnFlex.date }]}>Date</Text>
                        <Text style={[styles.tableCellHeader, { flex: columnFlex.day }]}>Day</Text>
                        <Text style={[styles.tableCellHeader, { flex: columnFlex.site }]}>Site</Text>
                        <Text style={[styles.tableCellHeader, { flex: columnFlex.designation }]}>Designation</Text>
                        <Text style={[styles.tableCellHeader, { flex: columnFlex.shiftStart }]}>Site-In</Text>
                        <Text style={[styles.tableCellHeader, { flex: columnFlex.shiftEnd }]}>Site-Out</Text>
                        <Text style={[styles.tableCellHeader, { flex: columnFlex.totalHours }]}>Total Hours</Text>
                        <Text style={[styles.tableCellHeader, { flex: columnFlex.breakHours }]}>Break Hours</Text>
                        <Text style={[styles.tableCellHeader, { flex: columnFlex.workingHours }]}>Working Hours</Text>
                    </View>

                    {data.length > 0 ? (
                        data.map((entry, i) => (
                            <View key={i} style={styles.tableRow}>
                                <Text style={[styles.tableCell, { flex: columnFlex.index }]}>{i + 1}</Text>
                                <Text style={[styles.tableCell, { flex: columnFlex.date }]}>{entry.date}</Text>
                                <Text style={[styles.tableCell, { flex: columnFlex.day }]}>{entry.day}</Text>
                                <Text style={[styles.tableCell, { flex: columnFlex.site }]}>{entry.siteDetails?.[0]?.name || '-'}</Text>
                                <Text style={[styles.tableCell, { flex: columnFlex.designation }]}>{entry.roleName}</Text>
                                <Text style={[styles.tableCell, { flex: columnFlex.shiftStart }]}>{entry.entryTime}</Text>
                                <Text style={[styles.tableCell, { flex: columnFlex.shiftEnd }]}>{entry.exitTime}</Text>
                                <Text style={[styles.tableCell, { flex: columnFlex.totalHours }]}>{entry.totalHours || '0 mins'}</Text>
                                <Text style={[styles.tableCell, { flex: columnFlex.breakHours }]}>{entry.breakHours || '0 mins'}</Text>
                                <Text style={[styles.tableCell, { flex: columnFlex.workingHours }]}>{entry.workingHours || '0 mins'}</Text>
                            </View>
                        ))
                    ) : (
                        <View style={styles.tableRow}>
                            <Text style={[styles.tableCell, { flex: 1, textAlign: 'center' }]}>No timesheet data found</Text>
                        </View>
                    )}
                </View>
            </Page>
        </Document>
    );
};

export default TimesheetPDF;
